import type { IImage } from 'src/types/image';
import type { DialogProps } from '@mui/material/Dialog';

import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';

import { useImageUrlToFile } from 'src/hooks/use-convert-image-url-to-file';

import { getAspectRatio } from 'src/utils/helper';
import {
  getImageUrl,
  getImageThumbnail,
  AMUSE_MAX_IMAGE_SIZE,
  AMUSE_SUPPORTED_IMAGE_FORMATS,
} from 'src/utils/file-service';

import { usePlaceImageSearch, useGetImagesForPlace } from 'src/actions/image';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';

import { useAuthContext } from 'src/auth/hooks';

import { ImageCrop } from '../image-crop';
import { Upload, UploadBox } from '../upload';

import type { ThumbnailArea } from '../image-crop';

function FilePreview({ file }: { file: File }) {
  const [src, setSrc] = useState<string>('');
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.neutral',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <img src={src} alt="preview" style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }} />
    </Box>
  );
}

// ----------------------------------------------------------------------

export type ImageUploadDialogProps = DialogProps & {
  placeGUID?: string;
  showExistingPlaceImages?: boolean;
  showImageSearch?: boolean;
  imageFile?: File;
  onClose: () => void;
  onUpload?: (
    image: File,
    altText: string,
    thumbnailArea?: ThumbnailArea,
    imageURL?: string,
    sourcePlaceImageGuid?: string
  ) => void;
  title?: string;
  maxFileSize?: number; // in MB
  acceptedFileTypes?: string[];
  cropAspect?: number;
};

export function ImageUploadDialog({
  placeGUID,
  showExistingPlaceImages = false,
  showImageSearch = false,
  imageFile,
  onClose,
  onUpload,
  title = 'Upload image',
  maxFileSize = AMUSE_MAX_IMAGE_SIZE / (1024 * 1024), // Convert bytes to MB
  acceptedFileTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  cropAspect,
  open,
  ...other
}: ImageUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(imageFile || null);
  const [selectedPlaceImage, setSelectedPlaceImage] = useState<IImage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isFetchingImage, setIsFetchingImage] = useState(false);
  const [fetchedImageFile, setFetchedImageFile] = useState<File | null>(null);
  const initialCroppedAreaRef = useRef<ThumbnailArea | null>(null);
  const [thumbnailArea, setThumbnailArea] = useState<ThumbnailArea | null>(null);
  const [altText, setAltText] = useState<string>('');
  const [downloadedPlaceImageFile, setDownloadedPlaceImageFile] = useState<File | null>(null);

  const { convertImageUrlToFile } = useImageUrlToFile();
  const { userProfile } = useAuthContext();
  const {
    searchPlaceImage,
    isSearching,
    error: placeImageSearchError,
  } = usePlaceImageSearch();

  useEffect(() => {
    if (imageFile) {
      setSelectedFile(imageFile);
    }
  }, [imageFile]);

  // Reset all states when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedFile(imageFile || null);
      setSelectedPlaceImage(null);
      setError(null);
      setTabValue(0);
      setIsDownloading(false);
      setImageUrl('');
      setIsFetchingImage(false);
      setFetchedImageFile(null);
      setThumbnailArea(null);
      setAltText('');
      setDownloadedPlaceImageFile(null);
    }
  }, [open, imageFile]);

  // Fetch place data and images
  const { images } = useGetImagesForPlace(placeGUID, 'place');

  const handleFileSelect = (file: File) => {
    setError(null);

    // Validate file type
    if (!acceptedFileTypes.includes(file.type)) {
      setError(`Invalid file type. Please select: ${acceptedFileTypes.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > maxFileSize * 1024 * 1024) {
      setError(`File size must be less than ${maxFileSize}MB`);
      return;
    }

    setSelectedFile(file);
  };

  const handleFetchImage = async () => {
    if (!imageUrl.trim()) {
      setError('Please enter an image URL');
      return;
    }

    setIsFetchingImage(true);
    setError(null);

    try {
      const fetchedFile = await convertImageUrlToFile(imageUrl);

      if (fetchedFile) {
        // Validate file type
        if (!acceptedFileTypes.includes(fetchedFile.type)) {
          setError(`Invalid file type. Please use: ${acceptedFileTypes.join(', ')}`);
          return;
        }

        // Validate file size
        if (fetchedFile.size > maxFileSize * 1024 * 1024) {
          setError(`File size must be less than ${maxFileSize}MB`);
          return;
        }

        setFetchedImageFile(fetchedFile);
      } else {
        setError('Failed to fetch image from URL');
      }
    } catch (err) {
      console.error('Error fetching image:', err);
      setError('Failed to fetch image from URL');
    } finally {
      setIsFetchingImage(false);
    }
  };

  const handleUpload = () => {
    if (selectedFile && onUpload && (!cropAspect || thumbnailArea)) {
      onUpload(selectedFile, altText, thumbnailArea ?? undefined);
      onClose();
    }
  };

  const handleFetchedImageUpload = () => {
    if (fetchedImageFile && onUpload && (!cropAspect || thumbnailArea)) {
      onUpload(fetchedImageFile, altText, thumbnailArea ?? undefined, imageUrl);
      onClose();
    }
  };

  const fileTipText = useMemo(() => {
    const aspectRatioText = cropAspect ? getAspectRatio(cropAspect) : '';
    return `Maximum file size: ${maxFileSize}MB. Supported formats: ${Object.values(AMUSE_SUPPORTED_IMAGE_FORMATS).flat().join(', ')}, cropping allowed. Aspect ratio: ${aspectRatioText}.`;
  }, [maxFileSize, cropAspect]);

  const fileTooltip = useCallback(
    () => (
      <Tooltip title={fileTipText}>
        <Iconify icon="eva:info-outline" width={16} sx={{ verticalAlign: 'middle' }} />
      </Tooltip>
    ),
    [fileTipText]
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: 'Upload Image',
        icon: <Iconify icon="eva:image-fill" />,
        tabValue: 0,
        endIcon: fileTooltip(),
      },
      {
        label: 'Image Link',
        icon: <Iconify icon="eva:link-2-fill" />,
        tabValue: 1,
      },
    ];

    let nextTabValue = 2;

    if (showExistingPlaceImages && placeGUID && images && images.length > 0) {
      baseTabs.push({
        label: 'Place Images',
        icon: <Iconify icon="mdi:location-on" />,
        tabValue: nextTabValue,
        endIcon: (
          <Label
            variant="soft"
            color={images.length > 0 ? 'primary' : 'default'}
            fontWeight={500}
          >
            {images.length}
          </Label>
        ),
      });
      nextTabValue += 1;
    }

    if (showImageSearch && userProfile?.hasBetaAccess && placeGUID) {
      baseTabs.push({
        label: 'Image Search',
        icon: <Iconify icon="mdi:wand" />,
        tabValue: nextTabValue,
      });
      nextTabValue += 1;
    }

    return baseTabs;
  }, [placeGUID, images, userProfile?.hasBetaAccess, fileTooltip, showExistingPlaceImages, showImageSearch]);

  const theme = useTheme();
  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
      {...other}
      sx={{ minHeight: '512px' }}
    >
      <DialogContent sx={{ px: 2 }}>
        <Grid container>
          <Grid item xs={12} md={4}>
            <Stack
              direction="column"
              spacing={1}
              sx={{ py: 2, borderRight: 1, borderColor: 'divider', pr: 2, height: '100%' }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Image type
              </Typography>
              {tabs.map((tab) => (
                <Button
                  key={tab.tabValue}
                  variant={tabValue === tab.tabValue ? 'contained' : 'text'}
                  onClick={() => setTabValue(tab.tabValue)}
                  startIcon={tab.icon}
                  sx={{
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    gap: 1,
                    p: 1.5,
                    color: theme.palette.grey[600],
                    backgroundColor:
                      tabValue === tab.tabValue ? theme.palette.grey[300] : 'transparent',
                    '&:hover': {
                      backgroundColor: theme.palette.grey[300],
                    },
                    fontWeight: 500,
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <span>{tab.label}</span>
                    {tab.endIcon && <Box sx={{ ml: 'auto' }}>{tab.endIcon}</Box>}
                  </Box>
                </Button>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={8} sx={{ py: 2, pl: 2 }}>
            <Box display={tabValue === 0 ? 'block' : 'none'} sx={{ minHeight: '400px' }}>
              <Stack spacing={3}>
                {!selectedFile && (
                  <>
                    <Upload
                      maxSize={AMUSE_MAX_IMAGE_SIZE}
                      accept={AMUSE_SUPPORTED_IMAGE_FORMATS}
                      onDrop={(acceptedFiles) => {
                        if (acceptedFiles.length > 0) {
                          handleFileSelect(acceptedFiles[0]);
                        }
                      }}
                      type="image"
                      disabled={false}
                    />
                    <Button variant="contained" color="primary" disabled>
                      Upload image
                    </Button>
                  </>
                )}

                {selectedFile && selectedFile instanceof File && (
                  <>
                    {cropAspect ? (
                    <ImageCrop
                      image={selectedFile}
                      aspect={cropAspect}
                      onCropComplete={(croppedArea) => {
                        setThumbnailArea(croppedArea);
                      }}
                      initialCroppedArea={initialCroppedAreaRef.current}
                    />
                    ) : (
                      <FilePreview file={selectedFile} />
                    )}
                    <Stack direction="row" spacing={1.5}>
                      <Stack direction="column" spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">Replace</Typography>
                          {fileTooltip()}
                        </Stack>
                        <UploadBox
                          value={selectedFile}
                          onDrop={(acceptedFiles) => {
                            if (acceptedFiles.length > 0) {
                              handleFileSelect(acceptedFiles[0]);
                            }
                          }}
                          disabled={false}
                          maxSize={AMUSE_MAX_IMAGE_SIZE}
                          accept={AMUSE_SUPPORTED_IMAGE_FORMATS}
                        />
                      </Stack>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">Alt Text</Typography>
                        <TextField
                          name="altText"
                          placeholder="Describe this image..."
                          disabled={false}
                          value={altText}
                          onChange={(e) => setAltText(e.target.value)}
                        />
                      </Stack>
                    </Stack>
                    <Button variant="contained" color="primary" onClick={handleUpload}>
                      Upload image
                    </Button>
                  </>
                )}

                {error && (
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box display={tabValue === 1 ? 'block' : 'none'} sx={{ minHeight: '400px' }}>
              <Stack spacing={3}>
                {!fetchedImageFile && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle2">Image URL</Typography>
                    <TextField
                      name="imageUrl"
                      placeholder="https://"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      disabled={isFetchingImage}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleFetchImage}
                      disabled={!imageUrl.trim() || isFetchingImage}
                      startIcon={
                        isFetchingImage ? (
                          <Iconify icon="eva:loader-outline" />
                        ) : (
                          <Iconify icon="eva:download-fill" />
                        )
                      }
                    >
                      {isFetchingImage ? 'Fetching...' : 'Fetch Image'}
                    </Button>
                  </Stack>
                )}

                {fetchedImageFile && (
                  <>
                    {cropAspect ? (
                    <ImageCrop
                      image={fetchedImageFile}
                      aspect={cropAspect}
                      onCropComplete={(croppedArea) => {
                        setThumbnailArea(croppedArea);
                      }}
                      initialCroppedArea={initialCroppedAreaRef.current}
                    />
                    ) : (
                      <FilePreview file={fetchedImageFile} />
                    )}
                    <Stack direction="row" spacing={1.5}>
                      <Stack direction="column" spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">Replace URL</Typography>
                          <Tooltip title="Enter a new image URL to fetch a different image">
                            <Iconify icon="eva:info-outline" width={16} />
                          </Tooltip>
                        </Stack>
                        <TextField
                          name="imageUrl"
                          placeholder="Enter new image URL"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                        />
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleFetchImage}
                          disabled={!imageUrl.trim() || isFetchingImage}
                          startIcon={
                            isFetchingImage ? (
                              <Iconify icon="eva:loader-outline" />
                            ) : (
                              <Iconify icon="eva:download-fill" />
                            )
                          }
                        >
                          {isFetchingImage ? 'Fetching...' : 'Fetch New Image'}
                        </Button>
                      </Stack>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">Alt Text</Typography>
                        <TextField
                          name="altText"
                          placeholder="Describe this image..."
                          disabled={false}
                          value={altText}
                          onChange={(e) => setAltText(e.target.value)}
                        />
                      </Stack>
                    </Stack>
                    <Button variant="contained" color="primary" onClick={handleFetchedImageUpload}>
                      Upload Image
                    </Button>
                  </>
                )}

                {error && (
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                )}
              </Stack>
            </Box>

            { showExistingPlaceImages && placeGUID && images && images.length > 0 && (
              <Box
                display={
                  tabValue === tabs.find((tab) => tab.label === 'Place Images')?.tabValue
                    ? 'block'
                    : 'none'
                }
                sx={{ minHeight: '400px' }}
              >
                <Stack spacing={3}>
                  {!downloadedPlaceImageFile && (
                    <>
                      <Grid container spacing={2}>
                        {images && images.length > 0 ? (
                        images.map((img: IImage) => (
                          <Grid item xs={6} sm={4} md={3} key={img.guid}>
                            <Box
                              onClick={() => setSelectedPlaceImage(img)}
                              sx={{
                                cursor: 'pointer',
                                border: selectedPlaceImage?.guid === img.guid ? 2 : 1,
                                borderColor:
                                  selectedPlaceImage?.guid === img.guid
                                    ? 'primary.main'
                                    : 'divider',
                                borderRadius: 1,
                                p: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  aspectRatio: '1',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              >
                                <img
                                  src={getImageThumbnail(img.s3Guid)}
                                  alt={img.alt}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            No images found for this place.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!selectedPlaceImage || isDownloading}
                      onClick={async () => {
                        if (selectedPlaceImage) {
                          setIsDownloading(true);
                          setError(null);
                          try {
                            // Get the full image URL using the s3Guid
                            const selectedPlaceUrl = getImageUrl(selectedPlaceImage);
                            // Download the image as a File
                            const selectedImageFile = await convertImageUrlToFile(selectedPlaceUrl);

                            if (selectedImageFile) {
                              setDownloadedPlaceImageFile(selectedImageFile);
                              setAltText(selectedPlaceImage.alt || '');
                            } else {
                              setError('Failed to download image');
                            }
                          } catch (err) {
                            console.error('Error downloading image:', err);
                            setError('Failed to download image');
                          } finally {
                            setIsDownloading(false);
                          }
                        }
                      }}
                      sx={{ mt: 2 }}
                    >
                      {isDownloading ? 'Downloading...' : 'Select image'}
                    </Button>
                  </>
                )}

                {downloadedPlaceImageFile && (
                  <>
                    {cropAspect ? (
                    <ImageCrop
                      image={downloadedPlaceImageFile}
                      aspect={cropAspect}
                      onCropComplete={(croppedArea) => {
                        setThumbnailArea(croppedArea);
                      }}
                      initialCroppedArea={thumbnailArea}
                    />
                    ) : (
                      <FilePreview file={downloadedPlaceImageFile} />
                    )}
                    <Stack direction="row" spacing={1.5}>
                      <Stack direction="column" spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">Select Different Image</Typography>
                          <Tooltip title="Go back to select a different place image">
                            <Iconify icon="eva:info-outline" width={16} />
                          </Tooltip>
                        </Stack>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            setDownloadedPlaceImageFile(null);
                            setSelectedPlaceImage(null);
                            setThumbnailArea(null);
                            setAltText('');
                          }}
                        >
                          Choose Different Image
                        </Button>
                      </Stack>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">Alt Text</Typography>
                        <TextField
                          name="altText"
                          placeholder="Describe this image..."
                          disabled={false}
                          value={altText}
                          onChange={(e) => setAltText(e.target.value)}
                        />
                      </Stack>
                    </Stack>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (onUpload && downloadedPlaceImageFile && (!cropAspect || thumbnailArea)) {
                          onUpload(downloadedPlaceImageFile, altText, thumbnailArea ?? undefined, undefined, selectedPlaceImage?.guid);
                          onClose();
                        }
                      }}
                    >
                      Finish Cropping
                    </Button>
                  </>
                )}

                  {error && (
                    <Typography variant="body2" color="error">
                      {error}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            {showImageSearch && userProfile?.hasBetaAccess && placeGUID && (
              <Box
                display={
                  tabValue === tabs.find((tab) => tab.label === 'Image Search')?.tabValue
                    ? 'block'
                    : 'none'
                }
                sx={{ minHeight: '400px' }}
              >
                <Stack spacing={3}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle2">  
                      Search for Images in the Public Domain
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Feature is only available to users with beta access.
                    </Typography>

                    
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        try {
                          await searchPlaceImage(placeGUID!);
                          onClose();
                        } catch (err) {
                          console.error('Image search failed:', err);
                          setError('Image search failed');
                        }
                      }}
                      disabled={isSearching}
                      startIcon={
                        isSearching ? (
                          <Iconify icon="eva:loader-outline" />
                        ) : (
                          <Iconify icon="mdi:wand" />
                        )
                      }
                      sx={{ mt: 2 }}
                    >
                      {isSearching ? 'Finding...' : 'Search for image'}
                    </Button>
                  </Stack>

                  {(error || placeImageSearchError) && (
                    <Typography variant="body2" color="error">
                      {error || placeImageSearchError}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
